import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Badge,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Checkbox,
  Fab,
  CircularProgress,
} from '@mui/material';
import {
  Build,
  Assignment,
  CheckCircle,
  Warning,
  PlayArrow,
  Refresh,
  Visibility,
  Person,
  Schedule,
  ExpandMore,
  ExpandLess,
  Scale,
  LocalShipping,
  Send,
} from '@mui/icons-material';
import { samplesService, Sample, SampleStatus, Priority } from '../../services/samplesService';
import { transfersService, locationsService, Location } from '../../services/locationsService';
import { isSatellite } from '../../config/location.config';

interface PreparationStats {
  total_samples: number;
  in_preparation: number;
  completed_today: number;
  overdue_samples: number;
  crushing_queue: number;
  grinding_queue: number;
  sieving_queue: number;
  ready_for_transfer: number; // 🛰️ Satellite: samples with all mech steps done
}

interface PreparationMecaniqueDashboardProps {
  userRole: 'ANALYSTE' | 'TECHNICIEN' | 'SUPERVISEUR';
  userId: string;
}

const PreparationMecaniqueDashboard: React.FC<PreparationMecaniqueDashboardProps> = ({
  userRole,
  userId,
}) => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [stats, setStats] = useState<PreparationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'start_crushing' | 'start_grinding' | 'start_sieving' | 'complete_preparation'>('start_crushing');
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  
  // Batch validation states
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<string>>(new Set());
  const [batchValidationDialogOpen, setBatchValidationDialogOpen] = useState(false);
  const [batchValidationLoading, setBatchValidationLoading] = useState(false);
  const [batchValidationProgress, setBatchValidationProgress] = useState(0);
  const [batchValidationError, setBatchValidationError] = useState<string | null>(null);
  
  // NEW: Weight entry modal states
  const [weightEntryDialogOpen, setWeightEntryDialogOpen] = useState(false);
  const [selectedBatchForWeights, setSelectedBatchForWeights] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [batchWeights, setBatchWeights] = useState<Record<string, number>>({});
  // 🔬 SCALE INTEGRATION: Track which samples have scale-recorded weights
  const [scaleWeightInfo, setScaleWeightInfo] = useState<Record<string, { hasScaleWeight: boolean; recordedAt?: string }>>({});

  // 🚚 TRANSFER: State for initiating transfers from the dashboard
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferBatchInfo, setTransferBatchInfo] = useState<{ batchNumber: string; samples: Sample[] } | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [destinationLocation, setDestinationLocation] = useState<Location | null>(null);

  // 🚚 TRANSFER: Fetch location config on mount (satellite only)
  useEffect(() => {
    if (isSatellite()) {
      const loadLocationConfig = async () => {
        try {
          const currentResponse = await locationsService.getCurrent();
          if (currentResponse) {
            setCurrentLocation(currentResponse);
            // The /locations/current endpoint returns transfer_destination as a nested object
            // Use it directly if available, otherwise fall back to sends_to + getById
            const transferDest = (currentResponse as any).transfer_destination;
            if (transferDest && transferDest.id) {
              setDestinationLocation(transferDest as Location);
            } else if (currentResponse.sends_to) {
              try {
                const dest = await locationsService.getById(currentResponse.sends_to);
                setDestinationLocation(dest);
              } catch (e) {
                console.error('Failed to load destination location:', e);
              }
            }
          }
        } catch (e) {
          console.error('Failed to load current location config:', e);
        }
      };
      loadLocationConfig();
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    // Set up auto-refresh every 30 seconds, but STOP when weight entry modal is open
    const interval = setInterval(() => {
      // 🔒 CRITICAL FIX: Don't refresh when weight entry modal is open to prevent data loss
      if (!weightEntryDialogOpen && !batchValidationDialogOpen && !actionDialogOpen && !transferDialogOpen) {
        loadDashboardData();
      }
    }, 30000); // Increased to 30 seconds to be less intrusive
    return () => clearInterval(interval);
  }, [weightEntryDialogOpen, batchValidationDialogOpen, actionDialogOpen, transferDialogOpen]);

  // Helper function to get current mechanical step for a sample
  const getCurrentMechanicalStep = (sample: Sample): string | null => {
    const methods = sample.metadonnees?.mechanical_preparation_methods || [];
    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
    
    // Define the sequential order
    const stepOrder = [
      'PM-PRE.MO/ANA/013-01', // Concassage
      'PM-PRE.MO/ANA/013-02', // Broyage  
      'PM-PRE.MO/ANA/013-03'  // Tamisage
    ];
    
    // Find the next step that needs to be done
    for (const step of stepOrder) {
      if (methods.includes(step) && !completedSteps.includes(step)) {
        return step;
      }
    }
    
    return null; // All steps completed
  };

  // 🛰️ SATELLITE: Helper function to check if ALL mechanical steps are complete
  const isMechanicalPrepComplete = (sample: Sample): boolean => {
    const methods = sample.metadonnees?.mechanical_preparation_methods || [];
    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
    
    if (methods.length === 0) return false;
    
    // All required methods must be in completed steps
    return methods.every((method: string) => completedSteps.includes(method));
  };

  // Helper function to check if sample should appear in current tab
  const shouldShowInTab = (sample: Sample, tabIndex: number): boolean => {
    const currentStep = getCurrentMechanicalStep(sample);
    
    switch (tabIndex) {
      case 0: return currentStep === 'PM-PRE.MO/ANA/013-01'; // Concassage tab
      case 1: return currentStep === 'PM-PRE.MO/ANA/013-02'; // Broyage tab
      case 2: return currentStep === 'PM-PRE.MO/ANA/013-03'; // Tamisage tab
      case 3: return true; // "Tous" tab shows everything
      case 4: return isMechanicalPrepComplete(sample); // 🛰️ "Ready for Transfer" tab (satellite only)
      default: return false;
    }
  };

  // NEW: Helper function to check if this is the first mechanical step for a sample
  const isFirstMechanicalStep = (sample: Sample): boolean => {
    const methods = sample.metadonnees?.mechanical_preparation_methods || [];
    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
    const currentStep = getCurrentMechanicalStep(sample);
    
    // Define the sequential order
    const stepOrder = [
      'PM-PRE.MO/ANA/013-01', // Concassage
      'PM-PRE.MO/ANA/013-02', // Broyage  
      'PM-PRE.MO/ANA/013-03'  // Tamisage
    ];
    
    // Find the first method that's required
    const firstRequiredMethod = stepOrder.find(step => methods.includes(step));
    
    // This is the first step if:
    // 1. No steps have been completed yet
    // 2. AND the current step is the first required method
    return completedSteps.length === 0 && currentStep === firstRequiredMethod;
  };

  // NEW: Check if any sample in batch needs weight entry (first mechanical step)
  const batchNeedsWeightEntry = (batchSamples: Sample[]): boolean => {
    return batchSamples.some(sample => isFirstMechanicalStep(sample));
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // 🚀 PERFORMANCE: Load ALL samples for preparation mecanique (no limit for production)
      // 🔬 BGM FIX: Fetch from multiple statuses to include BGM samples in other lab workflows
      // First get total count
      const countResponse = await samplesService.getSamples(1, 1, {
        status: [
          SampleStatus.PREPARATION_MECANIQUE,            // Regular samples
          SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,    // BGM samples in AU workflow
          SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,   // BGM samples in ICP workflow
          SampleStatus.LABORATOIRE_AU_ANALYSE,          // BGM samples in AU analysis
          SampleStatus.LABORATOIRE_ICP_ANALYSE,         // BGM samples in ICP analysis
        ],
      });
      const totalSamples = countResponse.total || 10000;
      
      // Then fetch all samples from multiple statuses
      const samplesResponse = await samplesService.getSamples(1, totalSamples, {
        status: [
          SampleStatus.PREPARATION_MECANIQUE,            // Regular samples
          SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,    // BGM samples in AU workflow
          SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,   // BGM samples in ICP workflow
          SampleStatus.LABORATOIRE_AU_ANALYSE,          // BGM samples in AU analysis
          SampleStatus.LABORATOIRE_ICP_ANALYSE,         // BGM samples in ICP analysis
        ],
      });
      
      // 🔬 BGM FIX: Filter samples based on METADATA, not just status
      // Show samples that need mechanical prep, regardless of their current lab status
      const preparationSamples = samplesResponse.samples.filter(sample => {
        const hasMechanicalMethods = sample.metadonnees?.mechanical_preparation_methods?.length > 0;
        if (!hasMechanicalMethods) return false;
        
        // 🛰️ SATELLITE FIX: On satellite sites, show ALL samples in PREPARATION_MECANIQUE status
        // This includes BGM samples that have completed mechanical prep but are waiting for transfer
        if (isSatellite()) {
          // On satellite: show if status is PREPARATION_MECANIQUE (regardless of BGM completion flag)
          return sample.statut === SampleStatus.PREPARATION_MECANIQUE;
        }
        
        // For MAIN LAB: BGM samples that completed mech prep should appear in their lab dashboards
        if (sample.metadonnees?.bgm_method_selected === true) {
          // Show BGM samples that haven't completed mechanical prep, regardless of lab status
          return !sample.metadonnees?.bgm_mechanical_prep_completed;
        }
        
        // For regular samples, check status
        return sample.statut === SampleStatus.PREPARATION_MECANIQUE;
      });
      
      // Group samples by batch
      const groupedSamples = groupSamplesByBatch(preparationSamples);
      
      setSamples(preparationSamples);
      
      // Calculate real statistics using SEQUENTIAL LOGIC
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      const realStats: PreparationStats = {
        total_samples: preparationSamples.length,
        in_preparation: preparationSamples.filter(s => s.statut === SampleStatus.PREPARATION_MECANIQUE).length,
        completed_today: preparationSamples.filter(s => 
          s.date_fin_preparation && 
          new Date(s.date_fin_preparation) >= todayStart
        ).length,
        overdue_samples: preparationSamples.filter(s => {
          if (!s.date_reception) return false;
          const receptionDate = new Date(s.date_reception);
          const daysSinceReception = Math.floor((now.getTime() - receptionDate.getTime()) / (1000 * 60 * 60 * 24));
          return daysSinceReception > 2; // Consider overdue if more than 2 days
        }).length,
        // FIXED: Only count samples currently AT each step
        crushing_queue: preparationSamples.filter(s => getCurrentMechanicalStep(s) === 'PM-PRE.MO/ANA/013-01').length,
        grinding_queue: preparationSamples.filter(s => getCurrentMechanicalStep(s) === 'PM-PRE.MO/ANA/013-02').length,
        sieving_queue: preparationSamples.filter(s => getCurrentMechanicalStep(s) === 'PM-PRE.MO/ANA/013-03').length,
        // 🛰️ SATELLITE: Count samples with all mechanical steps completed (ready for transfer)
        ready_for_transfer: preparationSamples.filter(s => isMechanicalPrepComplete(s)).length,
      };

      setStats(realStats);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty state on error
      setSamples([]);
      setStats({
        total_samples: 0,
        in_preparation: 0,
        completed_today: 0,
        overdue_samples: 0,
        crushing_queue: 0,
        grinding_queue: 0,
        sieving_queue: 0,
        ready_for_transfer: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  // Check if sample is part of a batch
  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  // Get batch number for sample
  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  // Get batch position for sample
  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  // Get total samples in batch
  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const getMethodDisplay = (methodCode: string): string => {
    const methods: Record<string, string> = {
      'PM-PRE.MO/ANA/013-01': 'Concassage',
      'PM-PRE.MO/ANA/013-02': 'Broyage',
      'PM-PRE.MO/ANA/013-03': 'Tamisage',
    };
    return methods[methodCode] || methodCode;
  };

  const handleStartAction = (sample: Sample, action: typeof actionType) => {
    setSelectedSample(sample);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleCompleteAction = async () => {
    if (!selectedSample) return;

    try {
      const currentStep = getCurrentMechanicalStep(selectedSample);
      
      if (!currentStep) {
        console.error('No current mechanical step found for sample');
        return;
      }

      // Mark current step as completed
      const completedSteps = selectedSample.metadonnees?.mechanical_preparation_completed_steps || [];
      const updatedCompletedSteps = [...completedSteps, currentStep];
      
      // Check if there's a next mechanical step
      const nextStep = getCurrentMechanicalStep({
        ...selectedSample,
        metadonnees: {
          ...selectedSample.metadonnees,
          mechanical_preparation_completed_steps: updatedCompletedSteps
        }
      });

      let nextStatus = selectedSample.statut;
      let statusMessage = '';

      if (nextStep) {
        // Stay in mechanical preparation for next step
        nextStatus = SampleStatus.PREPARATION_MECANIQUE;
        statusMessage = `Étape ${getMethodDisplay(currentStep)} terminée - Prochaine étape: ${getMethodDisplay(nextStep)}`;
      } else {
        // All mechanical steps completed - move to laboratory
        nextStatus = determineNextLaboratoryStatus(selectedSample);
        statusMessage = `Toute la préparation mécanique terminée - Envoi vers laboratoire`;
      }

      // Update sample with completed step and new status
      await samplesService.updateSample(selectedSample.id, {
        statut: nextStatus,
        metadonnees: {
          ...selectedSample.metadonnees,
          mechanical_preparation_completed_steps: updatedCompletedSteps,
          [`${currentStep}_completed_at`]: new Date().toISOString(),
          [`${currentStep}_completed_by`]: userId
        }
      });
      
      console.log(`✅ Step completed: ${currentStep} → Next: ${nextStep || 'Laboratory'}`);
      
      setActionDialogOpen(false);
      setSelectedSample(null);
      loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating sample status:', error);
    }
  };

  // Function to determine which laboratory chemical preparation stage based on selected methods
  const determineNextLaboratoryStatus = (sample: Sample): SampleStatus => {
    // 🛰️ SATELLITE SITE: Don't route to lab - samples need transfer first!
    // At satellite sites, samples stay in PREPARATION_MECANIQUE after completion
    // They will be transferred to main lab via mobile app, then routed to labs there
    if (isSatellite()) {
      console.log('🛰️ Satellite site detected - keeping sample in PREPARATION_MECANIQUE for transfer');
      console.log('   Sample will appear in "Ready for Transfer" section');
      return SampleStatus.PREPARATION_MECANIQUE;
    }

    // Check if sample has selected methods in metadata
    const selectedMethods = sample.metadonnees?.analyses_demandees || [];
    const selectedLaboratoryMethods = sample.metadonnees?.selected_laboratory_methods || [];
    
    console.log('🔄 Determining next laboratory for sample:', sample.sample_id);
    console.log('   Selected methods:', selectedMethods);
    console.log('   BGM method selected:', sample.metadonnees?.bgm_method_selected);
    console.log('   Multi-lab workflow:', sample.metadonnees?.multi_lab_workflow);

    // 🔬 CHECK FOR ANY MULTI-LAB WORKFLOW (BGM or custom routing)
    const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true;
    const isBGMSample = sample.metadonnees?.bgm_method_selected === true || 
                       selectedMethods.includes('BGM-MULTI-LAB');
    
    if (isMultiLabSample || isBGMSample) {
      console.log('🔬 Multi-lab sample detected - routing based on element_lab_routing');
      
      // Get the unique laboratories from element routing
      const elementRouting = sample.metadonnees?.element_lab_routing || {};
      const uniqueLabs = [...new Set(Object.values(elementRouting))] as string[];
      
      console.log('   Element routing:', elementRouting);
      console.log('   Unique labs:', uniqueLabs);
      
      // Priority order for first lab: ICP → SAA → AU → VOLUMETRIE
      if (uniqueLabs.includes('LABORATOIRE_ICP')) {
        console.log('   → Routing to ICP chemical preparation (multi-lab)');
        return SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE;
      } else if (uniqueLabs.includes('LABORATOIRE_SAA')) {
        console.log('   → Routing to SAA chemical preparation (multi-lab)');
        return SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE;
      } else if (uniqueLabs.includes('LABORATOIRE_AU')) {
        console.log('   → Routing to AU chemical preparation (multi-lab)');
        return SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE;
      } else if (uniqueLabs.includes('LABORATOIRE_VOLUMETRIE')) {
        console.log('   → Routing to Volumétrie chemical preparation (multi-lab)');
        return SampleStatus.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE;
      }
      
      // Fallback for multi-lab: default to ICP
      console.log('   → Multi-lab fallback: routing to ICP');
      return SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE;
    }

    // Check method codes to determine laboratory (regular non-multi-lab samples)
    const hasIcpMethods = selectedMethods.some((method: string) => 
      method.includes('ICP') || method.startsWith('ICP-')
    );
    
    const hasSaaMethods = selectedMethods.some((method: string) => 
      method.includes('AA-') || method.includes('SAA') || method.startsWith('AA-')
    );
    
    const hasAuMethods = selectedMethods.some((method: string) => 
      method.includes('Au-') || method.startsWith('Au-')
    );
    
    const hasVolumetrieMethods = selectedMethods.some((method: string) => 
      method.includes('VOL-') || method.includes('TITRATION') || method.startsWith('VOL-')
    );

    // Priority order: ICP -> SAA -> AU -> Volumétrie (handle multi-lab samples)
    if (hasIcpMethods) {
      console.log('   → Routing to ICP chemical preparation');
      return SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE;
    } else if (hasSaaMethods) {
      console.log('   → Routing to SAA chemical preparation');
      return SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE;
    } else if (hasAuMethods) {
      console.log('   → Routing to AU chemical preparation');
      return SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE;
    } else if (hasVolumetrieMethods) {
      console.log('   → Routing to Volumétrie chemical preparation');
      return SampleStatus.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE;
    }
    
    // Fallback: default to SAA if no specific methods detected
    console.log('   → No specific methods detected, defaulting to SAA chemical preparation');
    return SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE;
  };

  const getPriorityColor = (priority: Priority): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      default: return 'default';
    }
  };

  const getActionButton = (sample: Sample) => {
    const currentStep = getCurrentMechanicalStep(sample);
    
    if (userRole === 'ANALYSTE' || userRole === 'TECHNICIEN') {
      const stepName = currentStep ? getMethodDisplay(currentStep) : 'Préparation';
      
      return (
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<PlayArrow />}
          onClick={() => handleStartAction(sample, 'complete_preparation')}
          disabled={!currentStep}
        >
          Terminer {stepName}
        </Button>
      );
    }
    
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<Visibility />}
        onClick={() => setSelectedSample(sample)}
      >
        Voir Détails
      </Button>
    );
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // Batch selection functions
  const toggleSampleSelection = (sampleId: string) => {
    const newSelected = new Set(selectedSampleIds);
    if (newSelected.has(sampleId)) {
      newSelected.delete(sampleId);
    } else {
      newSelected.add(sampleId);
    }
    setSelectedSampleIds(newSelected);
  };

  const selectAllVisibleSamples = () => {
    const filteredSamples = samples.filter(sample => shouldShowInTab(sample, selectedTab));
    const visibleIds = filteredSamples.map(s => s.id);
    setSelectedSampleIds(new Set(visibleIds));
  };

  const clearSelection = () => {
    setSelectedSampleIds(new Set());
  };

  const handleBatchValidation = async () => {
    try {
      setBatchValidationLoading(true);
      setBatchValidationProgress(0);
      setBatchValidationError(null);

      const selectedSamples = samples.filter(s => selectedSampleIds.has(s.id));
      
      if (selectedSamples.length === 0) {
        throw new Error('Aucun échantillon sélectionné pour la validation');
      }

      console.log(`🚀 PERFORMANCE: Starting bulk validation for ${selectedSamples.length} samples`);

      // Group by current step to ensure we're validating the same step
      const samplesByStep = selectedSamples.reduce((acc, sample) => {
        const currentStep = getCurrentMechanicalStep(sample);
        if (currentStep) {
          if (!acc[currentStep]) acc[currentStep] = [];
          acc[currentStep].push(sample);
        }
        return acc;
      }, {} as Record<string, Sample[]>);

      const totalSteps = Object.keys(samplesByStep).length;
      let completedSteps = 0;

      // 🚀 PERFORMANCE: Process all steps in parallel with progress tracking
      const bulkUpdatePromises = Object.entries(samplesByStep).map(async ([step, stepSamples]) => {
        try {
          // Prepare bulk update data for this step
          const bulkUpdates = stepSamples.map(sample => {
            const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
            const updatedCompletedSteps = [...completedSteps, step];
            
            // Check if there's a next mechanical step
            const nextStep = getCurrentMechanicalStep({
              ...sample,
              metadonnees: {
                ...sample.metadonnees,
                mechanical_preparation_completed_steps: updatedCompletedSteps
              }
            });

            let nextStatus = sample.statut;
            if (!nextStep) {
              // All mechanical steps completed - move to laboratory
              nextStatus = determineNextLaboratoryStatus(sample);
            }

            // Check if this is a BGM sample
            const isBGMSample = sample.metadonnees?.bgm_method_selected === true || 
                               (sample.metadonnees?.analyses_demandees || []).includes('BGM-MULTI-LAB');

            return {
              id: sample.id,
              statut: nextStatus,
              metadonnees: {
                ...sample.metadonnees,
                mechanical_preparation_completed_steps: updatedCompletedSteps,
                [`${step}_completed_at`]: new Date().toISOString(),
                [`${step}_completed_by`]: userId,
                batch_validation_notes: `Validation en lot de ${stepSamples.length} échantillons - ${step}`,
                // 🔬 BGM SPECIFIC: Mark mechanical prep completed for multi-lab workflow
                ...(isBGMSample && !nextStep && {
                  bgm_mechanical_prep_completed: true,
                  bgm_mechanical_prep_completed_at: new Date().toISOString(),
                  bgm_ready_for_multi_lab: true,
                  // Ensure element lab routing is preserved (should be set during reception)
                  element_lab_routing: sample.metadonnees?.element_lab_routing || {
                    'Au': 'LABORATOIRE_AU',
                    'Ag': 'LABORATOIRE_ICP',
                    'As': 'LABORATOIRE_ICP',
                    'Cu': 'LABORATOIRE_ICP',
                    'Fe': 'LABORATOIRE_ICP',
                    'Mo': 'LABORATOIRE_ICP',
                    'Pb': 'LABORATOIRE_ICP',
                    'Sn': 'LABORATOIRE_ICP',
                    'Zn': 'LABORATOIRE_ICP'
                  }
                })
              }
            };
          });

          // 🚀 PERFORMANCE: Use Promise.all for parallel updates within each step
          console.log(`🔄 Processing ${stepSamples.length} samples for step: ${step}`);
          
          // Process in chunks of 50 to avoid overwhelming the server
          const chunkSize = 50;
          const chunks = [];
          for (let i = 0; i < bulkUpdates.length; i += chunkSize) {
            chunks.push(bulkUpdates.slice(i, i + chunkSize));
          }

          for (const chunk of chunks) {
            // FIXED: Use bulk update method like weight entry
            const sampleIds = chunk.map(updateData => updateData.id);
            const metadataUpdates: Record<string, any> = {};
            const statusUpdates: Array<{ sampleId: string; status: SampleStatus }> = [];
            
            chunk.forEach(updateData => {
              // Store metadata updates (sample-specific)
              metadataUpdates[updateData.id] = updateData.metadonnees;
              
              // Store status updates if changed
              const currentSample = stepSamples.find(s => s.id === updateData.id);
              if (currentSample && updateData.statut !== currentSample.statut) {
                statusUpdates.push({ sampleId: updateData.id, status: updateData.statut });
              }
            });
            
            // Update metadata first
            await samplesService.updateSampleBatch(sampleIds, metadataUpdates);
            
            // Then update statuses if needed
            for (const statusUpdate of statusUpdates) {
              await samplesService.updateSampleBatch([statusUpdate.sampleId], {}, {
                status: statusUpdate.status,
                comment: `Validation en lot - ${step} complété`
              });
            }
          }

          // Update progress
          completedSteps++;
          setBatchValidationProgress((completedSteps / totalSteps) * 100);

        } catch (stepError) {
          console.error(`❌ Error processing step ${step}:`, stepError);
          throw new Error(`Erreur lors du traitement de l'étape ${step}: ${(stepError as Error).message}`);
        }
      });

      // 🚀 PERFORMANCE: Wait for all steps to complete in parallel
      await Promise.all(bulkUpdatePromises);

      console.log(`✅ PERFORMANCE: Bulk validation completed for ${selectedSamples.length} samples`);
      
      // Clear selection and refresh
      clearSelection();
      setBatchValidationDialogOpen(false);
      loadDashboardData();
      
    } catch (error) {
      console.error('❌ Error in batch validation:', error);
      setBatchValidationError((error as Error).message || 'Erreur lors de la validation en lot');
    } finally {
      setBatchValidationLoading(false);
      setBatchValidationProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Atelier de Préparation Mécanique - Chargement...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Build color="primary" />
          Atelier de Préparation Mécanique
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDashboardData}
          >
            Actualiser
          </Button>
          <Chip
            label={`Rôle: ${userRole}`}
            color="primary"
            icon={<Person />}
          />
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Échantillons
                </Typography>
                <Typography variant="h4">
                  {stats.total_samples}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Préparation
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.in_preparation}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  File Concassage
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.crushing_queue}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Terminés Aujourd'hui
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed_today}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Alerts */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>🔄 Workflow Séquentiel:</strong> Les échantillons progressent étape par étape : 
          <strong>Concassage → Broyage → Tamisage</strong>. Un échantillon n'apparaît que dans l'onglet de son étape actuelle.
          {isSatellite() && (
            <><br/><strong>🛰️ Site Satellite:</strong> Après la préparation mécanique, les échantillons apparaissent dans l'onglet "Prêt pour Transfert" pour être envoyés au laboratoire principal.</>
          )}
        </Typography>
      </Alert>
      
      {stats && stats.overdue_samples > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{stats.overdue_samples}</strong> échantillon(s) en retard nécessitent une attention immédiate.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => {
            setSelectedTab(newValue);
            clearSelection(); // Clear selection when changing tabs
          }}
        >
          <Tab label="Concassage" icon={<Badge badgeContent={stats?.crushing_queue} color="warning"><Build /></Badge>} />
          <Tab label="Broyage" icon={<Badge badgeContent={stats?.grinding_queue} color="info"><Build /></Badge>} />
          <Tab label="Tamisage" icon={<Badge badgeContent={stats?.sieving_queue} color="success"><Build /></Badge>} />
          <Tab label="Tous" icon={<Assignment />} />
          {/* 🛰️ SATELLITE: Show "Ready for Transfer" tab only at satellite sites */}
          {isSatellite() && (
            <Tab 
              label="Prêt pour Transfert" 
              icon={
                <Badge badgeContent={stats?.ready_for_transfer} color="success">
                  <LocalShipping />
                </Badge>
              }
              sx={{ 
                backgroundColor: (stats?.ready_for_transfer || 0) > 0 ? 'rgba(76, 175, 80, 0.1)' : 'inherit',
                borderRadius: 1,
              }}
            />
          )}
        </Tabs>
      </Box>

      {/* Batch Actions Bar */}
      {selectedSampleIds.size > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#e3f2fd', border: '2px solid #2196f3' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {selectedSampleIds.size} échantillon(s) sélectionné(s)
                </Typography>
                <Chip 
                  label={`Étape: ${selectedTab === 0 ? 'Concassage' : selectedTab === 1 ? 'Broyage' : selectedTab === 2 ? 'Tamisage' : selectedTab === 4 ? 'Prêt pour Transfert' : 'Mixte'}`}
                  color="primary"
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectAllVisibleSamples}
                >
                  Tout Sélectionner
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearSelection}
                >
                  Tout Déselectionner
                </Button>
{(() => {
                  // Check if any selected samples need weight entry
                  const selectedSamples = samples.filter(s => selectedSampleIds.has(s.id));
                  const needsWeightEntry = selectedSamples.some(sample => isFirstMechanicalStep(sample));
                  
                  return (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => {
                        if (needsWeightEntry) {
                          // Group samples by batch for weight entry
                          const batchGroups = selectedSamples.reduce((acc, sample) => {
                            const batchNumber = getBatchNumber(sample);
                            if (batchNumber) {
                              if (!acc[batchNumber]) acc[batchNumber] = [];
                              acc[batchNumber].push(sample);
                            }
                            return acc;
                          }, {} as Record<string, Sample[]>);
                          
                          // For now, handle first batch (could be enhanced for multiple batches)
                          const firstBatchNumber = Object.keys(batchGroups)[0];
                          if (firstBatchNumber) {
                            // 🔧 FIX: Sort samples by sample_id in ascending order (R-4545-25-01, R-4545-25-02, etc.)
                            const sortedSamples = batchGroups[firstBatchNumber].sort((a, b) => {
                              // Extract the last number from sample_id (e.g., "01" from "R-4545-25-01")
                              const getLastNumber = (sampleId: string) => {
                                const parts = sampleId.split('-');
                                return parseInt(parts[parts.length - 1]) || 0;
                              };
                              return getLastNumber(a.sample_id) - getLastNumber(b.sample_id);
                            });
                            
                            setSelectedBatchForWeights({
                              batchNumber: firstBatchNumber,
                              samples: sortedSamples
                            });
                            
                                            // Initialize weights with current values or defaults
                                            // 🔬 SCALE INTEGRATION: Prioritize scale weights over manual
                                            const weights: Record<string, number> = {};
                                            const scaleInfo: Record<string, { hasScaleWeight: boolean; recordedAt?: string }> = {};
                                            batchGroups[firstBatchNumber].forEach(sample => {
                                              // Check for scale weight first (stored in grams, convert to kg)
                                              const scaleWeight = sample.metadonnees?.scale_weights?.preparation_mecanique?.weight_grams;
                                              const hasScaleWeight = scaleWeight !== undefined && scaleWeight !== null;
                                              
                                              if (hasScaleWeight) {
                                                weights[sample.id] = scaleWeight / 1000; // Convert grams to kg
                                                scaleInfo[sample.id] = {
                                                  hasScaleWeight: true,
                                                  recordedAt: sample.metadonnees?.scale_weights?.preparation_mecanique?.recorded_at
                                                };
                                              } else {
                                                weights[sample.id] = sample.metadonnees?.poids_kg || 2.5; // Default 2.5kg (reception weight)
                                                scaleInfo[sample.id] = { hasScaleWeight: false };
                                              }
                                            });
                                            setBatchWeights(weights);
                                            setScaleWeightInfo(scaleInfo);
                                            setWeightEntryDialogOpen(true);
                          }
                        } else {
                          setBatchValidationDialogOpen(true);
                        }
                      }}
                      startIcon={needsWeightEntry ? <Scale /> : <CheckCircle />}
                      disabled={selectedSampleIds.size === 0}
                      sx={{
                        background: needsWeightEntry 
                          ? 'linear-gradient(45deg, #2196f3, #1976d2)' 
                          : selectedSampleIds.size > 100 
                            ? 'linear-gradient(45deg, #ff9800, #f57c00)' 
                            : 'linear-gradient(45deg, #4caf50, #2e7d32)',
                        '&:hover': {
                          background: needsWeightEntry 
                            ? 'linear-gradient(45deg, #1976d2, #1565c0)' 
                            : selectedSampleIds.size > 100 
                              ? 'linear-gradient(45deg, #f57c00, #e65100)' 
                              : 'linear-gradient(45deg, #2e7d32, #1b5e20)',
                        }
                      }}
                    >
                      {needsWeightEntry 
                        ? `Préparer le Lot (${selectedSampleIds.size})`
                        : `Valider Lot (${selectedSampleIds.size})`
                      }
                      {selectedSampleIds.size > 100 && ' ⚡'}
                    </Button>
                  );
                })()}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sample Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedSampleIds.size > 0 && selectedSampleIds.size < samples.filter(s => shouldShowInTab(s, selectedTab)).length}
                      checked={samples.filter(s => shouldShowInTab(s, selectedTab)).length > 0 && selectedSampleIds.size === samples.filter(s => shouldShowInTab(s, selectedTab)).length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllVisibleSamples();
                        } else {
                          clearSelection();
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>ID Échantillon / Lot</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Méthodes Requises</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Progrès</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // FIXED: Filter samples by CURRENT STEP, not just method presence
                  const filteredSamples = samples.filter(sample => shouldShowInTab(sample, selectedTab));

                  // Then group by batch
                  const groupedItems = filteredSamples.reduce((acc, sample) => {
                    const batchNumber = getBatchNumber(sample);
                    if (batchNumber) {
                      // Group batch samples
                      const existingBatch = acc.find(item => item.type === 'batch' && item.batchNumber === batchNumber);
                      if (existingBatch) {
                        (existingBatch as {type: 'batch', batchNumber: string, samples: Sample[]}).samples.push(sample);
                      } else {
                        acc.push({
                          type: 'batch' as const,
                          batchNumber,
                          samples: [sample]
                        });
                      }
                    } else {
                      // Individual sample
                      acc.push({
                        type: 'individual' as const,
                        sample
                      });
                    }
                    return acc;
                  }, [] as Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}>);

                  // Render the grouped items
                  return groupedItems.map((item, index) => {
                    if (item.type === 'batch') {
                      // Render batch header
                      const batchItem = item as {type: 'batch', batchNumber: string, samples: Sample[]};
                      const firstSample = batchItem.samples[0];
                      const totalWeight = batchItem.samples.reduce((sum, s) => sum + (s.metadonnees?.poids_kg || 0), 0);
                      const isExpanded = expandedBatches.has(batchItem.batchNumber);
                      
                      return (
                        <React.Fragment key={`batch-${batchItem.batchNumber}`}>
                          {/* Batch Header Row - Now Clickable */}
                          <TableRow 
                            sx={{ 
                            backgroundColor: '#e3f2fd', 
                            '&:hover': { backgroundColor: '#bbdefb' },
                              borderLeft: '4px solid #2196f3',
                              cursor: 'pointer'
                            }}
                            onClick={() => toggleBatchExpansion(batchItem.batchNumber)}
                          >
                            <TableCell colSpan={8}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <IconButton size="small" sx={{ p: 0 }}>
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Box sx={{ 
                                    backgroundColor: '#2196f3', 
                                    color: 'white', 
                                    px: 2, 
                                    py: 0.5, 
                                    borderRadius: 1,
                                    fontWeight: 'bold'
                                  }}>
                                    LOT
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    {batchItem.batchNumber}
                                  </Typography>
                                  <Chip 
                                    label={`${batchItem.samples.length} échantillons`} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                  <Chip 
                                    label={`${totalWeight.toFixed(2)} kg total`} 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    Client: {firstSample.client?.company_name || 'N/A'}
                                  </Typography>
                                </Box>
                                
                                {/* NEW: Batch Action Button */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {(() => {
                                    const needsWeightEntry = batchNeedsWeightEntry(batchItem.samples);
                                    // 🛰️ SATELLITE: Check if ALL samples in batch are ready for transfer
                                    const allReadyForTransfer = batchItem.samples.every(s => isMechanicalPrepComplete(s));
                                    
                                    // 🚚 If all samples are ready for transfer, show TRANSFER button
                                    if (allReadyForTransfer && isSatellite()) {
                                      return (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          startIcon={transferLoading && transferBatchInfo?.batchNumber === batchItem.batchNumber ? <CircularProgress size={18} color="inherit" /> : <Send />}
                                          disabled={transferLoading || !currentLocation || !destinationLocation}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTransferBatchInfo({
                                              batchNumber: batchItem.batchNumber,
                                              samples: batchItem.samples,
                                            });
                                            setTransferError(null);
                                            setTransferSuccess(null);
                                            setTransferDialogOpen(true);
                                          }}
                                          sx={{
                                            fontWeight: 'bold',
                                            fontSize: '0.85rem',
                                            py: 1,
                                            px: 2.5,
                                            background: 'linear-gradient(45deg, #ff6f00, #e65100)',
                                            '&:hover': {
                                              background: 'linear-gradient(45deg, #e65100, #bf360c)',
                                            },
                                            '&.Mui-disabled': {
                                              background: '#bdbdbd',
                                            },
                                            color: 'white',
                                            boxShadow: '0 4px 12px rgba(230, 81, 0, 0.35)',
                                          }}
                                        >
                                          {transferLoading && transferBatchInfo?.batchNumber === batchItem.batchNumber
                                            ? 'Transfert...'
                                            : `Transférer → ${destinationLocation?.name || 'Main Lab'}`
                                          }
                                        </Button>
                                      );
                                    }
                                    
                                    if (needsWeightEntry) {
                                      return (
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          size="small"
                                          startIcon={<Scale />}
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent row expansion
                                            setSelectedBatchForWeights({
                                              batchNumber: batchItem.batchNumber,
                                              samples: batchItem.samples
                                            });
                                            
                                            // Initialize weights with current values or defaults
                                            // 🔬 SCALE INTEGRATION: Prioritize scale weights over manual
                                            const weights: Record<string, number> = {};
                                            const scaleInfo: Record<string, { hasScaleWeight: boolean; recordedAt?: string }> = {};
                                            batchItem.samples.forEach(sample => {
                                              // Check for scale weight first (stored in grams, convert to kg)
                                              const scaleWeight = sample.metadonnees?.scale_weights?.preparation_mecanique?.weight_grams;
                                              const hasScaleWeight = scaleWeight !== undefined && scaleWeight !== null;
                                              
                                              if (hasScaleWeight) {
                                                weights[sample.id] = scaleWeight / 1000; // Convert grams to kg
                                                scaleInfo[sample.id] = {
                                                  hasScaleWeight: true,
                                                  recordedAt: sample.metadonnees?.scale_weights?.preparation_mecanique?.recorded_at
                                                };
                                              } else {
                                                weights[sample.id] = sample.metadonnees?.poids_kg || 2.5;
                                                scaleInfo[sample.id] = { hasScaleWeight: false };
                                              }
                                            });
                                            setBatchWeights(weights);
                                            setScaleWeightInfo(scaleInfo);
                                            setWeightEntryDialogOpen(true);
                                          }}
                                          sx={{
                                            background: 'linear-gradient(45deg, #2196f3, #1976d2)',
                                            '&:hover': {
                                              background: 'linear-gradient(45deg, #1976d2, #1565c0)',
                                            }
                                          }}
                                        >
                                          Préparer le Lot
                                        </Button>
                                      );
                                    } else {
                                      return (
                                        <Button
                                          variant="contained"
                                          color="success"
                                          size="small"
                                          startIcon={<CheckCircle />}
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent row expansion
                                            // Select all samples in this batch for validation
                                            const batchSampleIds = new Set(batchItem.samples.map(s => s.id));
                                            setSelectedSampleIds(batchSampleIds);
                                            setBatchValidationDialogOpen(true);
                                          }}
                                          sx={{
                                            background: 'linear-gradient(45deg, #4caf50, #2e7d32)',
                                            '&:hover': {
                                              background: 'linear-gradient(45deg, #2e7d32, #1b5e20)',
                                            }
                                          }}
                                        >
                                          Valider le Lot
                                        </Button>
                                      );
                                    }
                                  })()}
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                          
                          {/* Individual Sample Rows - Only show when expanded */}
                          {isExpanded && batchItem.samples.map((sample, sampleIndex) => (
                            <TableRow 
                              key={sample.id}
                              sx={{ 
                                backgroundColor: '#f8f9fa',
                                borderLeft: '2px solid #2196f3',
                                ml: 2
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedSampleIds.has(sample.id)}
                                  onChange={() => toggleSampleSelection(sample.id)}
                                  disabled={!getCurrentMechanicalStep(sample)}
                                />
                              </TableCell>
                              <TableCell sx={{ pl: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {sample.sample_id}
                                  </Typography>
                                  <Chip 
                                    label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip label={sample.type_echantillon} size="small" />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(sample.metadonnees?.mechanical_preparation_methods || []).map((method: string, methodIndex: number) => {
                                    const currentStep = getCurrentMechanicalStep(sample);
                                    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
                                    const isCurrentStep = method === currentStep;
                                    const isCompleted = completedSteps.includes(method);
                                    
                                    return (
                                    <Chip 
                                      key={methodIndex} 
                                      label={getMethodDisplay(method)} 
                                      size="small" 
                                        variant={isCurrentStep ? "filled" : "outlined"}
                                      sx={{ 
                                          backgroundColor: isCompleted ? '#4caf50' : (isCurrentStep ? '#ff9800' : 'transparent'),
                                          color: isCompleted || isCurrentStep ? 'white' : '#666',
                                          border: isCurrentStep ? '2px solid #ff9800' : (isCompleted ? '1px solid #4caf50' : '1px solid #ccc'),
                                        '& .MuiChip-label': { fontWeight: 'bold' }
                                      }}
                                        icon={isCompleted ? <CheckCircle sx={{ fontSize: 16 }} /> : (isCurrentStep ? <PlayArrow sx={{ fontSize: 16 }} /> : undefined)}
                                    />
                                    );
                                  })}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={sample.priorite}
                                  size="small"
                                  color={getPriorityColor(sample.priorite)}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {(() => {
                                    const methods = sample.metadonnees?.mechanical_preparation_methods || [];
                                    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
                                    const totalSteps = methods.length;
                                    const completedCount = completedSteps.length;
                                    const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
                                    
                                    return (
                                      <>
                                  <LinearProgress 
                                    variant="determinate" 
                                          value={progressPercent} 
                                    sx={{ width: 60 }}
                                          color={progressPercent === 100 ? 'success' : 'primary'}
                                  />
                                        <Typography variant="caption">
                                          {completedCount}/{totalSteps}
                                        </Typography>
                                      </>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {getActionButton(sample)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    } else {
                      // Render individual sample
                      const individualItem = item as {type: 'individual', sample: Sample};
                      const sample = individualItem.sample;
                      return (
                    <TableRow key={sample.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSampleIds.has(sample.id)}
                          onChange={() => toggleSampleSelection(sample.id)}
                          disabled={!getCurrentMechanicalStep(sample)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sample.sample_id}
                        </Typography>
                      </TableCell>
                          <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={sample.type_echantillon} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sample.metadonnees?.mechanical_preparation_methods || []).map((method: string, methodIndex: number) => {
                            const currentStep = getCurrentMechanicalStep(sample);
                            const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
                            const isCurrentStep = method === currentStep;
                            const isCompleted = completedSteps.includes(method);
                            
                            return (
                            <Chip 
                                  key={methodIndex} 
                              label={getMethodDisplay(method)} 
                              size="small" 
                                variant={isCurrentStep ? "filled" : "outlined"}
                              sx={{ 
                                  backgroundColor: isCompleted ? '#4caf50' : (isCurrentStep ? '#ff9800' : 'transparent'),
                                  color: isCompleted || isCurrentStep ? 'white' : '#666',
                                  border: isCurrentStep ? '2px solid #ff9800' : (isCompleted ? '1px solid #4caf50' : '1px solid #ccc'),
                                '& .MuiChip-label': { fontWeight: 'bold' }
                              }}
                                icon={isCompleted ? <CheckCircle sx={{ fontSize: 16 }} /> : (isCurrentStep ? <PlayArrow sx={{ fontSize: 16 }} /> : undefined)}
                            />
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                              label={sample.priorite}
                          size="small"
                              color={getPriorityColor(sample.priorite)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {(() => {
                            const methods = sample.metadonnees?.mechanical_preparation_methods || [];
                            const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
                            const totalSteps = methods.length;
                            const completedCount = completedSteps.length;
                            const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
                            
                            return (
                              <>
                          <LinearProgress 
                            variant="determinate" 
                                  value={progressPercent} 
                            sx={{ width: 60 }}
                                  color={progressPercent === 100 ? 'success' : 'primary'}
                          />
                                <Typography variant="caption">
                                  {completedCount}/{totalSteps}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getActionButton(sample)}
                      </TableCell>
                    </TableRow>
                      );
                    }
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Batch Validation Dialog */}
      <Dialog 
        open={batchValidationDialogOpen} 
        onClose={() => !batchValidationLoading && setBatchValidationDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {batchValidationLoading ? (
              <CircularProgress size={24} />
            ) : (
              <CheckCircle color="primary" />
            )}
            Validation en Lot - Préparation Mécanique
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {batchValidationError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Erreur:</strong> {batchValidationError}
                </Typography>
              </Alert>
            )}

            {batchValidationLoading ? (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Validation en cours...</strong><br/>
                    Traitement de {selectedSampleIds.size} échantillon(s). Veuillez patienter.
                  </Typography>
                </Alert>
                <LinearProgress 
                  variant="determinate" 
                  value={batchValidationProgress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  {Math.round(batchValidationProgress)}% terminé
                </Typography>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Validation de {selectedSampleIds.size} échantillon(s)</strong><br/>
                  Cette action va marquer l'étape actuelle comme terminée pour tous les échantillons sélectionnés
                  et les faire progresser vers l'étape suivante ou le laboratoire.
                </Typography>
              </Alert>
            )}

            {/* Show samples to be validated */}
            <Typography variant="h6" gutterBottom>
              Échantillons à valider:
            </Typography>
            <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
              {samples.filter(s => selectedSampleIds.has(s.id)).map(sample => {
                const currentStep = getCurrentMechanicalStep(sample);
                return (
                  <Box key={sample.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {sample.sample_id}
                    </Typography>
                    <Chip 
                      label={currentStep ? getMethodDisplay(currentStep) : 'Aucune étape'}
                      size="small"
                      color={currentStep ? 'primary' : 'default'}
                    />
                  </Box>
                );
              })}
            </Paper>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes de validation (optionnel)"
              placeholder="Ajoutez des commentaires sur cette validation en lot..."
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Qualité de la préparation</InputLabel>
              <Select defaultValue="GOOD">
                <MenuItem value="EXCELLENT">Excellente</MenuItem>
                <MenuItem value="GOOD">Bonne</MenuItem>
                <MenuItem value="ACCEPTABLE">Acceptable</MenuItem>
                <MenuItem value="REQUIRES_REWORK">Nécessite reprise</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setBatchValidationDialogOpen(false)}
            disabled={batchValidationLoading}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleBatchValidation} 
            variant="contained" 
            size="large"
            startIcon={batchValidationLoading ? <CircularProgress size={20} /> : <CheckCircle />}
            disabled={batchValidationLoading || selectedSampleIds.size === 0}
          >
            {batchValidationLoading 
              ? `Validation en cours...` 
              : `Valider ${selectedSampleIds.size} Échantillon(s)`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Weight Entry Dialog for Batch Preparation */}
      <Dialog 
        open={weightEntryDialogOpen} 
        onClose={() => setWeightEntryDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Scale color="primary" />
            Saisie des Poids du Lot
          </Typography>
          {selectedBatchForWeights && (
            <Typography variant="subtitle2" color="text.secondary">
              Lot: {selectedBatchForWeights.batchNumber} ({selectedBatchForWeights.samples.length} échantillons)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Saisie des poids</strong><br/>
                Entrez le poids en kilogrammes pour chaque échantillon du lot. 
                Ces poids apparaîtront dans la génération de certificats.
              </Typography>
            </Alert>

            {/* 🔬 SCALE INTEGRATION: Show info if any samples have scale weights */}
            {Object.values(scaleWeightInfo).some(info => info.hasScaleWeight) && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>⚖️ Poids de balance détectés</strong><br/>
                  Certains échantillons ont des poids enregistrés par la balance (bordure verte). 
                  Vous pouvez les modifier si nécessaire.
                </Typography>
              </Alert>
            )}

            {selectedBatchForWeights && (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        N°
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Référence Lab
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Référence Client
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 150 }}>
                        Poids (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBatchForWeights.samples
                      .sort((a, b) => {
                        // 🔧 CRITICAL FIX: Sort samples by sample_id in ascending order
                        const getLastNumber = (sampleId: string) => {
                          const parts = sampleId.split('-');
                          return parseInt(parts[parts.length - 1]) || 0;
                        };
                        return getLastNumber(a.sample_id) - getLastNumber(b.sample_id);
                      })
                      .map((sample, index) => (
                      <TableRow 
                        key={sample.id}
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {sample.sample_id}
                        </TableCell>
                        <TableCell>
                          {sample.external_id || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={batchWeights[sample.id] || ''}
                              onChange={(e) => {
                                const value = e.target.value ? parseFloat(e.target.value) : 0;
                                setBatchWeights(prev => ({
                                  ...prev,
                                  [sample.id]: value
                                }));
                                // 🔬 SCALE: If user manually edits, clear scale indicator
                                if (scaleWeightInfo[sample.id]?.hasScaleWeight) {
                                  setScaleWeightInfo(prev => ({
                                    ...prev,
                                    [sample.id]: { ...prev[sample.id], hasScaleWeight: false }
                                  }));
                                }
                              }}
                              placeholder="2.5"
                              inputProps={{ 
                                min: 0, 
                                step: 0.1,
                                'aria-label': `Poids pour ${sample.sample_id}`,
                                style: { textAlign: 'center' }
                              }}
                              InputProps={{
                                endAdornment: (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    kg
                                  </Typography>
                                )
                              }}
                              sx={{ 
                                width: 110,
                                '& .MuiOutlinedInput-root': {
                                  // 🔬 SCALE INTEGRATION: Green border if weight came from scale
                                  ...(scaleWeightInfo[sample.id]?.hasScaleWeight && {
                                    '& fieldset': { borderColor: 'success.main', borderWidth: 2 }
                                  }),
                                  '&:hover fieldset': { borderColor: scaleWeightInfo[sample.id]?.hasScaleWeight ? 'success.dark' : 'primary.main' },
                                  '&.Mui-focused fieldset': { borderColor: scaleWeightInfo[sample.id]?.hasScaleWeight ? 'success.main' : 'primary.main' }
                                }
                              }}
                            />
                            {/* 🔬 SCALE INDICATOR: Show badge if weight came from scale */}
                            {scaleWeightInfo[sample.id]?.hasScaleWeight && (
                              <Tooltip 
                                title={`Poids de la balance - ${scaleWeightInfo[sample.id]?.recordedAt ? new Date(scaleWeightInfo[sample.id].recordedAt!).toLocaleString() : 'N/A'}`}
                                arrow
                              >
                                <Chip
                                  label="⚖️"
                                  size="small"
                                  color="success"
                                  sx={{ 
                                    height: 22, 
                                    fontSize: '0.7rem',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setWeightEntryDialogOpen(false)}
          >
            Annuler
          </Button>
          <Button 
            onClick={async () => {
              // Save weights and proceed with batch validation
              if (selectedBatchForWeights) {
                try {
                  // Save weights AND advance mechanical step
                  const sampleIds = selectedBatchForWeights.samples.map(s => s.id);
                  
                  // FIXED: Separate metadata updates and status updates for backend compatibility
                  const metadataUpdates: Record<string, any> = {};
                  const statusUpdates: Array<{ sampleId: string; status: SampleStatus }> = [];
                  
                  selectedBatchForWeights.samples.forEach(sample => {
                    const currentStep = getCurrentMechanicalStep(sample);
                    const completedSteps = sample.metadonnees?.mechanical_preparation_completed_steps || [];
                    const updatedCompletedSteps = currentStep ? [...completedSteps, currentStep] : completedSteps;
                    
                    // Check if there's a next mechanical step after marking current as completed
                    const nextStep = getCurrentMechanicalStep({
                      ...sample,
                      metadonnees: {
                        ...sample.metadonnees,
                        mechanical_preparation_completed_steps: updatedCompletedSteps
                      }
                    });

                    // Determine next status
                    let nextStatus = sample.statut; // Default: stay in PREPARATION_MECANIQUE
                    if (!nextStep) {
                      // All mechanical steps completed - move to laboratory
                      nextStatus = determineNextLaboratoryStatus(sample);
                    }
                    
                    // Check if this is a BGM sample
                    const isBGMSample = sample.metadonnees?.bgm_method_selected === true || 
                                       (sample.metadonnees?.analyses_demandees || []).includes('BGM-MULTI-LAB');
                    
                    // Store metadata updates (sample-specific)
                    metadataUpdates[sample.id] = {
                      poids_kg: batchWeights[sample.id] || 2.5,
                      mechanical_preparation_completed_steps: updatedCompletedSteps,
                      [`${currentStep}_completed_at`]: new Date().toISOString(),
                      [`${currentStep}_completed_by`]: userId,
                      preparation_notes: `Poids saisi et étape ${currentStep} complétée`,
                      // 🔬 BGM SPECIFIC: Mark mechanical prep completed for multi-lab workflow
                      ...(isBGMSample && !nextStep && {
                        bgm_mechanical_prep_completed: true,
                        bgm_mechanical_prep_completed_at: new Date().toISOString(),
                        bgm_ready_for_multi_lab: true,
                        // Ensure element lab routing is preserved (should be set during reception)
                        element_lab_routing: sample.metadonnees?.element_lab_routing || {
                          'Au': 'LABORATOIRE_AU',
                          'Ag': 'LABORATOIRE_ICP',
                          'As': 'LABORATOIRE_ICP',
                          'Cu': 'LABORATOIRE_ICP',
                          'Fe': 'LABORATOIRE_ICP',
                          'Mo': 'LABORATOIRE_ICP',
                          'Pb': 'LABORATOIRE_ICP',
                          'Sn': 'LABORATOIRE_ICP',
                          'Zn': 'LABORATOIRE_ICP'
                        }
                      })
                    };
                    
                    // Store status updates for later
                    if (nextStatus !== sample.statut) {
                      statusUpdates.push({ sampleId: sample.id, status: nextStatus });
                    }
                  });
                  
                  // Update metadata first
                  await samplesService.updateSampleBatch(sampleIds, metadataUpdates);
                  
                  // Then update statuses if needed
                  for (const statusUpdate of statusUpdates) {
                    await samplesService.updateSampleBatch([statusUpdate.sampleId], {}, {
                      status: statusUpdate.status,
                      comment: 'Progression automatique après préparation mécanique'
                    });
                  }
                  
                  // Close weight dialog only - samples should move to next step automatically
                  setWeightEntryDialogOpen(false);
                  
                  // Reload data to reflect weight changes and new step progression
                  // Add small delay to ensure database changes are committed
                  setTimeout(() => {
                    loadDashboardData();
                  }, 500);
                } catch (error) {
                  console.error('Error updating sample weights:', error);
                  // Could add error handling here
                }
              }
            }}
            variant="contained" 
            size="large"
            startIcon={<CheckCircle />}
            disabled={!selectedBatchForWeights || Object.keys(batchWeights).length === 0}
          >
            Enregistrer et Continuer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Terminer Préparation Mécanique
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Échantillon: <strong>{selectedSample?.sample_id}</strong>
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes sur la préparation"
              placeholder="Ajoutez vos notes sur le processus de préparation mécanique..."
              sx={{ mt: 2 }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Qualité de la préparation</InputLabel>
              <Select defaultValue="GOOD">
                <MenuItem value="EXCELLENT">Excellente</MenuItem>
                <MenuItem value="GOOD">Bonne</MenuItem>
                <MenuItem value="ACCEPTABLE">Acceptable</MenuItem>
                <MenuItem value="REQUIRES_REWORK">Nécessite reprise</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleCompleteAction} variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🚚 TRANSFER: Success notification */}
      {transferSuccess && (
        <Alert 
          severity="success" 
          onClose={() => setTransferSuccess(null)}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 9999,
            minWidth: 400,
            boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
          }}
        >
          <Typography variant="body1" fontWeight="bold">{transferSuccess}</Typography>
        </Alert>
      )}

      {/* 🚚 TRANSFER: Confirmation Dialog */}
      <Dialog 
        open={transferDialogOpen} 
        onClose={() => !transferLoading && setTransferDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalShipping color="warning" />
            Confirmer le Transfert
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {transferError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTransferError(null)}>
                {transferError}
              </Alert>
            )}

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Attention :</strong> Cette action va initier le transfert physique de ce lot.
                Les échantillons passeront au statut "En cours d'acheminement" et ne seront plus modifiables.
              </Typography>
            </Alert>

            {transferBatchInfo && (
              <Paper sx={{ p: 2, backgroundColor: '#fff3e0', border: '1px solid #ff9800' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Lot</Typography>
                    <Typography variant="h6" fontWeight="bold">{transferBatchInfo.batchNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Échantillons</Typography>
                    <Typography variant="h6" fontWeight="bold">{transferBatchInfo.samples.length}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Origine</Typography>
                    <Typography variant="body1" fontWeight="bold">{currentLocation?.name || 'Site actuel'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Destination</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">{destinationLocation?.name || 'Laboratoire principal'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Échantillons concernés :
              </Typography>
              <Paper sx={{ maxHeight: 150, overflow: 'auto', p: 1, mt: 1, backgroundColor: '#f5f5f5' }}>
                {transferBatchInfo?.samples.map(sample => (
                  <Chip 
                    key={sample.id}
                    label={sample.sample_id}
                    size="small"
                    sx={{ m: 0.3 }}
                  />
                ))}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setTransferDialogOpen(false)}
            disabled={transferLoading}
          >
            Annuler
          </Button>
          <Button 
            onClick={async () => {
              if (!transferBatchInfo || !currentLocation || !destinationLocation) return;
              
              setTransferLoading(true);
              setTransferError(null);
              
              try {
                const result = await transfersService.initiateTransfer({
                  batch_number: transferBatchInfo.batchNumber,
                  sample_ids: transferBatchInfo.samples.map(s => s.id),
                  from_location_id: currentLocation.id,
                  to_location_id: destinationLocation.id,
                  notes: `Transféré depuis le tableau de bord LIMS - Lot ${transferBatchInfo.batchNumber}`,
                });
                
                setTransferDialogOpen(false);
                setTransferSuccess(
                  `Transfert ${result.transfer.transfer_number || ''} initié avec succès ! ${result.samples.length} échantillons en cours d'acheminement vers ${destinationLocation.name}.`
                );
                
                // Auto-hide success after 8 seconds
                setTimeout(() => setTransferSuccess(null), 8000);
                
                // Refresh data to reflect the changes
                loadDashboardData();
                
              } catch (error: any) {
                const message = error?.response?.data?.message || error?.message || 'Erreur inconnue lors du transfert';
                setTransferError(typeof message === 'string' ? message : JSON.stringify(message));
              } finally {
                setTransferLoading(false);
              }
            }}
            variant="contained" 
            size="large"
            startIcon={transferLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            disabled={transferLoading || !currentLocation || !destinationLocation}
            sx={{
              background: 'linear-gradient(45deg, #ff6f00, #e65100)',
              '&:hover': { background: 'linear-gradient(45deg, #e65100, #bf360c)' },
              fontWeight: 'bold',
              px: 3,
            }}
          >
            {transferLoading ? 'Transfert en cours...' : `Confirmer le Transfert`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Batch Validation */}
      {selectedSampleIds.size > 0 && (
        <Fab
          color="primary"
          aria-label="validate batch"
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            background: `linear-gradient(45deg, #4caf50, #2e7d32)`,
            boxShadow: `0 8px 32px rgba(76, 175, 80, 0.4)`,
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 12px 40px rgba(76, 175, 80, 0.5)`,
            }
          }}
          onClick={() => setBatchValidationDialogOpen(true)}
        >
          <Badge badgeContent={selectedSampleIds.size} color="secondary">
            <CheckCircle sx={{ fontSize: 28, color: 'white' }} />
          </Badge>
        </Fab>
      )}
    </Box>
  );
};

export default PreparationMecaniqueDashboard; 